import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import {
    getMessaging,
    getToken,
    onMessage,
    deleteToken,
    isSupported as firebaseMessagingIsSupported,
} from 'firebase/messaging'
import * as Sentry from '@sentry/vue'
// import { userAuthUserStore } from '@/stores/user'

const firebaseConfig = {
    apiKey: import.meta.env.VITE_APP_FIREBASE_CONFIG_APIKEY,
    authDomain: import.meta.env.VITE_APP_FIREBASE_authDomain,
    projectId: import.meta.env.VITE_APP_FIREBASE_projectId,
    storageBucket: import.meta.env.VITE_APP_FIREBASE_storageBucket,
    messagingSenderId: import.meta.env.VITE_APP_FIREBASE_messagingSenderId,
    appId: import.meta.env.VITE_APP_FIREBASE_appId,
    measurementId: import.meta.env.VITE_APP_FIREBASE_measurementId,
}

const app = firebase.initializeApp(firebaseConfig)

let messagingPromise = null

const getMessagingInstance = async () => {
    if (!messagingPromise) {
        messagingPromise = (async () => {
            try {
                const supported = await firebaseMessagingIsSupported()
                if (!supported) {
                    return null
                }
                return getMessaging(app)
            } catch (error) {
                reportFirebaseInitIssue(error, 'is-supported-check')
                return null
            }
        })()
    }
    return messagingPromise
}

const reportFirebaseInitIssue = (error, context, extra = {}) => {
    try {
        const err =
            error instanceof Error
                ? error
                : new Error(
                      typeof error === 'string'
                          ? error
                          : JSON.stringify(error)
                  )
        Sentry?.captureException(err, {
            tags: {
                area: 'firebase-init',
                context,
            },
            extra,
        })
    } catch (_) {}
}

// Request permission and get FCM token
export const requestNotificationPermission = async () => {
    // If Notification API is not available, do nothing and return null
    if (typeof Notification === 'undefined') {
        console.warn('Notification API is not available in this environment.')
        return null
    }
    try {
        const messaging = await getMessagingInstance()
        if (!messaging) {
            console.warn('Firebase messaging is not supported in this browser.')
            return null
        }
        // Check if notification permission is already granted
        if (Notification.permission === 'granted') {
            return getFCMToken()
        }

        const permission = await Notification.requestPermission()
        if (permission === 'granted') {
            return getFCMToken()
        } else {
            console.log('Notification permission denied')
            return null
        }
    } catch (error) {
        console.error('Error requesting notification permission:', error)
        reportFirebaseInitIssue(error, 'request-notification-permission')
        return null
    }
}

// Helper: wait for service worker to be ready with a timeout
async function waitForServiceWorkerReady(timeoutMs = 30000) {
    if (!('serviceWorker' in navigator)) return null

    const start = Date.now()
    const pollInterval = 400

    const swUrl = `${import.meta.env.BASE_URL || '/'}sw.js`
    const swScope = import.meta.env.BASE_URL || '/'

    const getActiveRegistration = async () => {
        try {
            const registration = await navigator.serviceWorker.getRegistration()
            // We just need an active registration to get a token.
            // Requiring .controller implies the page is controlled, which might not happen immediately
            // if clients.claim() is missing or delayed.
            if (registration?.active) {
                return registration
            }
            return null
        } catch (error) {
            reportFirebaseInitIssue(
                error,
                'wait-for-service-worker-ready-get-registration'
            )
            return null
        }
    }

    let registration = await getActiveRegistration()
    if (registration) {
        return registration
    }

    // Ensure a registration exists; if not, try to register the Workbox service worker manually.
    try {
        await navigator.serviceWorker.register(swUrl, { scope: swScope })
    } catch (registerError) {
        reportFirebaseInitIssue(registerError, 'wait-for-service-worker-ready-register', {
            swUrl,
        })
    }

    // Kick off the ready promise so that any failures are captured, but continue polling.
    navigator.serviceWorker.ready.catch((error) => {
        reportFirebaseInitIssue(
            error,
            'wait-for-service-worker-ready-ready-rejection'
        )
    })

    while (Date.now() - start < timeoutMs) {
        registration = await getActiveRegistration()
        if (registration) {
            return registration
        }
        await new Promise((resolve) => setTimeout(resolve, pollInterval))
    }

    reportFirebaseInitIssue(new Error('SW ready timeout'), 'wait-for-service-worker-ready', {
        timeoutMs,
    })

    return null
}

// Helper: getToken with a timeout wrapper
async function getTokenWithTimeout(messaging, options, timeoutMs = 15000) {
    try {
        const token = await Promise.race([
            getToken(messaging, options),
            new Promise((_, reject) =>
                setTimeout(
                    () => reject(new Error('getToken timeout')),
                    timeoutMs
                )
            ),
        ])
        return token
    } catch (e) {
        console.warn('getToken failed or timed out:', e?.message || e)
        reportFirebaseInitIssue(e, 'get-token-with-timeout', {
            timeoutMs,
        })
        return null
    }
}

// Get FCM token
export const getFCMToken = async () => {
    try {
        const messaging = await getMessagingInstance()
        if (!messaging) {
            console.warn('Firebase messaging is not supported in this browser.')
            return null
        }
        if (!('serviceWorker' in navigator)) {
            console.warn('Service workers are not supported in this browser.')
            // Try to get a token anyway without an explicit registration
            return await getTokenWithTimeout(messaging, {
                vapidKey: import.meta.env.VITE_APP_FIREBASE_vapidKey,
            })
        }

        // Try to re-use the existing Workbox service worker, but do not block indefinitely
        const swRegistration = await waitForServiceWorkerReady(30000)
        if (!swRegistration || !swRegistration.active) {
            reportFirebaseInitIssue(
                new Error('No active service worker available'),
                'get-fcm-token-no-active-sw'
            )
            return null
        }

        // Send Firebase config to the active service worker. Safe to send repeatedly.
        try {
            swRegistration.active.postMessage({
                type: 'FIREBASE_CONFIG',
                config: firebaseConfig,
            })
        } catch (_) {}

        // Attempt token retrieval with whichever registration we have (or without one as fallback)
        const currentToken = await getTokenWithTimeout(messaging, {
            vapidKey: import.meta.env.VITE_APP_FIREBASE_vapidKey,
            ...(swRegistration
                ? { serviceWorkerRegistration: swRegistration }
                : {}),
        })

        if (currentToken) {
            return currentToken
        } else {
            console.log(
                'No registration token available. Request permission to generate one.'
            )
            reportFirebaseInitIssue(
                new Error('FCM returned null token'),
                'get-fcm-token-null-token'
            )
            return null
        }
    } catch (error) {
        console.error('Error getting FCM token:', error)
        reportFirebaseInitIssue(error, 'get-fcm-token')
        return null
    }
}

// Delete FCM token to effectively unsubscribe this device from push
export const deleteFCMToken = async () => {
    try {
        const messaging = await getMessagingInstance()
        if (!messaging) {
            console.warn('Firebase messaging is not supported in this browser.')
            return false
        }
        // deleteToken will remove the currently stored token for this messaging instance
        const success = await deleteToken(messaging)
        if (!success) {
            console.warn('No FCM token to delete or deletion was not needed.')
        }
        return true
    } catch (error) {
        console.error('Error deleting FCM token:', error)
        reportFirebaseInitIssue(error, 'delete-fcm-token')
        return false
    }
}

// Handle foreground messages (when app is open)
export const onForegroundMessage = async (callback) => {
    try {
        const messaging = await getMessagingInstance()
        if (!messaging) {
            return () => {}
        }
        return onMessage(messaging, (payload) => {
            console.log('Message received in the foreground:', payload)
            callback(payload)
        })
    } catch (error) {
        reportFirebaseInitIssue(error, 'foreground-message-handler')
        return () => {}
    }
}

export const isMessagingSupported = async () => {
    const messaging = await getMessagingInstance()
    return !!messaging
}

export { app }
